import type {
  BaseQueryFn,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'
import { retry, createApi } from '@reduxjs/toolkit/query/react'
import type { AxiosError, AxiosRequestConfig } from 'axios'
import { toast } from 'sonner'
import config from '@/config'
import { customAxios, isAxiosError } from '@/utils/axiosHelper'
import { ServiceErrorHandler } from '@/connect-types/backend/service'

const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: '' }
  ): BaseQueryFn<
    {
      url: string
      method: AxiosRequestConfig['method']
      data?: AxiosRequestConfig['data']
      params?: AxiosRequestConfig['params']
      headers?: AxiosRequestConfig['headers']
    },
    unknown,
    FetchBaseQueryError
  > =>
  async ({ url, method, data, params, headers }) => {
    try {
      let axiosUrl = baseUrl + url
      if (url.startsWith('https://')) {
        axiosUrl = url
      }
      const result = await customAxios({
        url: axiosUrl,
        method,
        data,
        params,
        headers,
      })
      return { data: result.data }
    } catch (axiosError) {
      const err = axiosError as AxiosError

      if (isAxiosError(err)) {
        const message = err.response?.data
          ? (err.response.data as any).error ||
            (err.response.data as any).message ||
            'Request failed'
          : err.message

        ServiceErrorHandler(
          err?.response?.status || 0,
          'Neo API Error',
          message,
          () => undefined
        )

        return {
          error: {
            status: err.response?.status,
            data: err.response?.data || err.message,
          },
        }
      }

      return {
        error: {
          status: 500,
          data: 'An unexpected error occurred',
        },
      }
    }
  }

const neoApi = createApi({
  reducerPath: 'neo',
  keepUnusedDataFor: 60 * 60 * 24,
  baseQuery: retry(axiosBaseQuery({ baseUrl: `${config.url.neo}/api` }), {
    maxRetries: 3,
  }),
  tagTypes: [
    'NeoSquareOrders',
    'NeoSquarePayments',
    'NeoSquareCatalog',
    'NeoSquareDevices',
    'NeoSquareCustomers',
    'NeoSquareTables',
    'NeoSquareIntegrations',
  ],
  endpoints: () => ({}),
})

export default neoApi
